import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BulkRemoveResult } from '@/api/types/evidence-provisioning.types';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/store/auth.store';
import type { CtsImplementationStatus, CtsReasonStarted, CtsWorkflowState } from '@grc/shared';
import type {
  TasksWorkspace, EvidenceEntry, ExceptionEntry, ActivityEntry, CtsProposedEvidenceItem,
} from '@/api/types/cts-tasks.types';

// ── TC4 — the Tasks workspace and the program surfaces ───────────────────────────────────────

export function useTasksWorkspace() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-tasks', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/workspace/tasks');
      return data.data as TasksWorkspace;
    },
    enabled: !!tenantId,
  });
}

/**
 * ⚠ A TRANSITION INVALIDATES THE ACTIVITY FEED TOO. Moving work writes a programme event, so a
 * feed left on a stale cache would show a workspace that had already moved on — and it would look
 * perfectly fine, which is what makes it worth wiring rather than leaving to a refresh.
 */
/**
 * ⚠ THE TENANT'S FIRST TOUCH — the create path the new model spent five slices without.
 *
 * Sends the durable control REFERENCE, which every workspace row already carries. The server
 * ensures the tenant's control state and then creates the work; the client never handles a state
 * id, because until TC6 nothing in the product produced one.
 */
export function useAssignControl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      controlReference: string; assigneeUserId: string; dueAt?: string | null;
      /** CAPR S2 — initiation (FR-N1): why the assessment was started, and the initiator's note. */
      reasonStarted?: CtsReasonStarted | null; initiationNote?: string | null;
    }) => {
      const { data } = await apiClient.post('/compliance/workspace/tasks', {
        controlReference: v.controlReference,
        assigneeUserId: v.assigneeUserId,
        dueAt: v.dueAt ?? null,
        reasonStarted: v.reasonStarted ?? null,
        initiationNote: v.initiationNote ?? null,
      });
      return data.data;
    },
    onSuccess: () => {
      // Assigning changes the CONTROLS list too (a control gains an open assignment), so both
      // caches move — a workspace showing stale counts beside fresh tasks looks broken.
      qc.invalidateQueries({ queryKey: ['cts-tasks'] });
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
      qc.invalidateQueries({ queryKey: ['cts-activity'] });
    },
  });
}

/** CAPR S5 — the server's per-control answer to a bulk re-assessment start. Every outcome is named. */
export interface BulkStartResult {
  outcome: 'all_started' | 'partial' | 'none_started';
  started: { reference: string; id: string }[];
  skipped: { reference: string; reason: string }[];
  failed: { reference: string; reason: string }[];
}

/**
 * ⚠ CAPR S5 — START A SCHEDULED RE-ASSESSMENT ON EACH SELECTED CONTROL. Each item carries its own
 * `dueAt`, computed by the caller with the single Start form's own calculation (dueAtForReview); the
 * server sets the reason (scheduled_review), starts each control in its own transaction with an audit
 * row, and names every skip and failure.
 */
export function useBulkStartReassessment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { assigneeUserId: string; items: { controlReference: string; dueAt: string | null }[] }) => {
      const { data } = await apiClient.post('/compliance/workspace/tasks/bulk-start', v);
      return data.data as BulkStartResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-tasks'] });
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
      qc.invalidateQueries({ queryKey: ['cts-activity'] });
    },
  });
}

/** Remove a control from the workspace — a withdrawal that keeps everything. */
export function useRemoveControl() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (controlReference: string) => {
      const { data } = await apiClient.post(
        `/compliance/workspace/controls/${controlReference}/remove`, {});
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
      qc.invalidateQueries({ queryKey: ['cts-frameworks'] });
    },
  });
}

/**
 * Remove many controls and REPORT what happened (P1.2 PD-D).
 *
 * ⚠ Replaces a loop of `useRemoveControl` calls fired from the browser with the answers thrown away.
 *   One request; the result names every control that was not removed.
 */
export function useBulkRemoveControls() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (controlReferences: string[]) => {
      const { data } = await apiClient.post('/compliance/workspace/controls/bulk-remove', { controlReferences });
      return data.data as BulkRemoveResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
      qc.invalidateQueries({ queryKey: ['cts-frameworks'] });
    },
  });
}

/** CAPR S2 — what a SUBMISSION carries: the assessor's proposal (the approver reviews exactly this). */
export interface CtsProposalPayload {
  implementationStatus: CtsImplementationStatus;
  rationale: string;
  /** ⓘ Only what the assessor writes: the server records who acknowledged it and the verification then. */
  evidence: Pick<CtsProposedEvidenceItem, 'artifactId' | 'acknowledgmentNote'>[];
}

/**
 * ⚠ CAPR S2 — THE PROPOSAL TRAVELS WITH THE SUBMIT, AND NOTHING TRAVELS WITH THE APPROVE. The body
 * is built per move: a submit sends the proposal; an approve sends `{to}` alone (the server approves
 * what was submitted and refuses a body that tries to supply a decision field); a return sends its
 * reason. Nothing else is ever sent.
 */
export function useTransitionTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; to: CtsWorkflowState; reason?: string; proposal?: CtsProposalPayload }) => {
      const body = v.to === 'submitted_for_review' && v.proposal
        ? { to: v.to, ...v.proposal }
        : v.to === 'returned'
          ? { to: v.to, reason: v.reason ?? null }
          : { to: v.to };
      const { data } = await apiClient.post(`/compliance/workspace/tasks/${v.id}/transition`, body);
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-tasks'] });
      qc.invalidateQueries({ queryKey: ['cts-activity'] });
      // The hub's Tasks tab reads the same rows.
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
    },
  });
}

/**
 * ⚠ REASSIGN IS NOT A TRANSITION, so it is not routed through useTransitionTask. It changes WHO
 * holds the work and leaves `workflow_status` exactly where it was — which is why the machine has
 * no arrow for it and `ctsNextStates()` will never offer it.
 */
export function useReassignTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; assigneeUserId: string }) => {
      const { data } = await apiClient.post(
        `/compliance/workspace/tasks/${v.id}/reassign`,
        { assigneeUserId: v.assigneeUserId },
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cts-tasks'] });
      qc.invalidateQueries({ queryKey: ['cts-activity'] });
      qc.invalidateQueries({ queryKey: ['cts-workspace'] });
    },
  });
}

export function useEvidenceRepository() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-evidence', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/workspace/evidence');
      return data.data.evidence as EvidenceEntry[];
    },
    enabled: !!tenantId,
  });
}

export function useExceptionsRegister() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-exceptions', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/workspace/exceptions');
      return data.data.exceptions as ExceptionEntry[];
    },
    enabled: !!tenantId,
  });
}

export function useProgramActivity() {
  const { tenantId } = useAuthStore();
  return useQuery({
    queryKey: ['cts-activity', tenantId],
    queryFn: async () => {
      const { data } = await apiClient.get('/compliance/workspace/activity');
      return data.data.activity as ActivityEntry[];
    },
    enabled: !!tenantId,
  });
}
